import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { translations } from "@/i18n";
import { useAccountData } from "@/hooks/useAccountData";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

const LS = {
  lang: "vv_lang",
  prefs: "vv_prefs",
  favs: "vv_favs",
  lists: "vv_lists",
};

function load(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
}

export function AppProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem(LS.lang) || "fr");
  const [prefs, setPrefsState] = useState(() => load(LS.prefs, { budget: 0, activities: [], neighborhoods: [] }));
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const { data, update, status: syncStatus, flush, clearPrivate, retrySync } = useAccountData(user, authChecked);
  const { favorites, lists } = data;
  const setFavorites = useCallback((next) => update((prev) => ({ ...prev, favorites: typeof next === "function" ? next(prev.favorites) : next })), [update]);
  const setLists = useCallback((next) => update((prev) => ({ ...prev, lists: typeof next === "function" ? next(prev.lists) : next })), [update]);

  const setLang = (l) => {
    setLangState(l);
    localStorage.setItem(LS.lang, l);
  };
  const setPrefs = (p) => {
    setPrefsState(p);
    localStorage.setItem(LS.prefs, JSON.stringify(p));
  };


  const t = useCallback((key) => translations[lang]?.[key] ?? translations.fr[key] ?? key, [lang]);

  const isFavorite = useCallback((id) => favorites.includes(id), [favorites]);
  const toggleFavorite = useCallback((id) => {
    setFavorites((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, [setFavorites]);

  const createList = useCallback((name) => {
    const list = { id: `list_${crypto.randomUUID()}`, name: name.trim().slice(0, 160), placeIds: [] };
    setLists((prev) => [...prev, list]);
    return list.id;
  }, [setLists]);
  const deleteList = useCallback((id) => setLists((prev) => prev.filter((l) => l.id !== id)), [setLists]);
  const toggleInList = useCallback((listId, placeId) => {
    setLists((prev) => prev.map((l) => {
      if (l.id !== listId) return l;
      const has = l.placeIds.includes(placeId);
      return { ...l, placeIds: has ? l.placeIds.filter((p) => p !== placeId) : [...l.placeIds, placeId] };
    }));
  }, [setLists]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setAuthChecked(true);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setAuthChecked(true);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    let unsaved = false;
    let timer;
    try {
      await Promise.race([flush(), new Promise((_, reject) => { timer = setTimeout(() => reject(new Error("sync_timeout")), 3000); })]);
    } catch { unsaved = true; } finally { clearTimeout(timer); }
    // A failed synchronization must never prevent session revocation.
    await api.post("/auth/logout", {}, { timeout: 10000 });
    clearPrivate();
    setUser(null);
    return { unsaved };
  }, [flush, clearPrivate]);

  const value = {
    lang, setLang, t,
    prefs, setPrefs,
    favorites, isFavorite, toggleFavorite,
    lists, createList, deleteList, toggleInList, setLists,
    user, setUser, authChecked, checkAuth, logout, synced: syncStatus === "synced", syncStatus, retrySync,
  };
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
